import React, { useEffect, useContext, useState, useCallback, useRef } from 'react';
import { getInitials } from '@fluentui/react';
import { SuiteHeaderContext } from '../SuiteHeaderProvider';
import {
  IMeControlConfiguration,
  IAccount,
  DisplayModeType,
  AccountType,
  AuthenticatedState,
  ICommand,
  CommandType,
  ThemeType
} from '@mecontrol/public-api/types';
import { FlexPanelCollections } from '../SuiteHeader.types';
import { addCDN, getAuthProviderConfig } from './UniversalMeControl.utils';
import { defaultBackgroundColor, ME_CONTROL_CDN, PARTNER_ID } from '../constants';
import { IUniversalMeControlProps } from './UniversalMeControl.types';
import { Color } from '../utils/Color';

const ME_CONTROL_CONTAINER_ID = 'me-control-container';

const UniversalMeControlBase: React.FC<IUniversalMeControlProps> = props => {
  const { theme, culture, userData, localizationMap, appId } = useContext(SuiteHeaderContext);
  const { leaveNewExp, switchOrganization } = localizationMap;
  const { name, tenantId, imageUrl, upn, tenantDisplayName } = userData;
  const {
    onLeavePreview,
    openOrganizationPanel,
    onSwitch,
    onSwitchTo,
    onSignOut,
    isMsalJsEnabled,
    evoBase,
    viewAccountLink
  } = props;
  const [isCdnReady, setCdnReady] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const meControlRef = useRef<HTMLDivElement>(null);
  const openOrganizationPanelRef = useRef(openOrganizationPanel);

  useEffect(() => {
    const color = new Color(theme?.backgroundColor || defaultBackgroundColor);

    setIsDarkTheme(color.IsDark());
  }, [theme?.backgroundColor]);

  useEffect(() => {
    openOrganizationPanelRef.current = openOrganizationPanel;
  }, [openOrganizationPanel]);

  const onOpenOrganizationPanel = useCallback(() => {
    const callback = openOrganizationPanelRef.current;
    callback && callback(FlexPanelCollections.organizations, true);
  }, []);

  useEffect(() => {
    // Add MeControl CDN
    addCDN(`${ME_CONTROL_CDN}partner=${PARTNER_ID}&market=${culture}`, () => {
      if (window.MeControl) {
        setCdnReady(true);
      } else {
        window.onMeControlReadyToLoad = () => setCdnReady(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isCdnReady) {
      return;
    }

    const meControlContainer = meControlRef.current;
    if (!meControlContainer) {
      throw new Error("Can't find me-control-container element");
    }

    // Clear the container of any placeholders
    meControlContainer.className = '';
    meControlContainer.innerHTML = '';

    const customLinks: ICommand[] = [
      {
        type: 'link' as CommandType,
        id: 'link1',
        text: switchOrganization,
        ariaLabel: switchOrganization,
        openInNewTab: false,
        onClick: onOpenOrganizationPanel
      }
    ];

    if (onLeavePreview) {
      customLinks.push({
        type: 'link' as CommandType,
        id: 'link2',
        text: leaveNewExp,
        ariaLabel: leaveNewExp,
        openInNewTab: false,
        onClick: onLeavePreview
      });
    }

    // Build configuration object
    const meControlOptions: IMeControlConfiguration = {
      apiGeneration: 'GEN2',
      containerId: ME_CONTROL_CONTAINER_ID,
      authProviderConfig: getAuthProviderConfig(
        { isMsalJsEnabled, evoBase, appId },
        { onSignOut, onSwitch, onSwitchTo }
      ),
      primaryUXConfig: {
        displayModes: [{ type: 'compressed' as DisplayModeType, widthBreakpoint: 1000000 }], // casting here and other lines below is because the exported enum is a const enum, which will be undefined in the run time if directly use it
        headerTheme: { type: isDarkTheme ? ('dark' as ThemeType) : ('light' as ThemeType) }
      },
      appContextConfig: {
        commands: customLinks
      }
    };

    if (upn) {
      // currentAccount should be undefined if user is signed out
      meControlOptions.currentAccount = {
        type: 'aad' as AccountType.AAD,
        authenticatedState: 'signedIn' as AuthenticatedState.SignedIn,
        displayName: name,
        memberName: upn,
        initials: getInitials(name, false, false),
        pictureUrl: imageUrl,
        tenantId,
        orgName: tenantDisplayName
      } as IAccount;
    } else {
      meControlOptions.currentAccount = undefined;
    }

    // Call load
    window.MeControl.Loader.loadAsync(meControlOptions).then(() => {
      // meControl hard codes the view account link to myaccount.microsoft.com, it doesn't support Mooncake, Fairfax host
      // temporary fix to override the hostname to the correct one
      // tracked by bug on Me Control team: https://dev.azure.com/identitydivision/Engineering/_workitems/edit/954214
      window.MeControl.API.addEventListener('controlexpanded', () => {
        const viewAccount = document.getElementById('mectrl_viewAccount') as HTMLAnchorElement;
        if (viewAccount && viewAccount.hostname !== viewAccountLink) {
          viewAccount.hostname = new URL(viewAccountLink).host;
        }
      });
    });
  }, [
    isCdnReady,
    isDarkTheme,
    name,
    upn,
    imageUrl,
    tenantId,
    tenantDisplayName,
    appId,
    evoBase,
    leaveNewExp,
    switchOrganization,
    isMsalJsEnabled,
    onOpenOrganizationPanel,
    onLeavePreview,
    onSwitch,
    onSwitchTo,
    onSignOut,
    viewAccountLink
  ]);

  return <div id={ME_CONTROL_CONTAINER_ID} ref={meControlRef}></div>;
};

export const UniversalMeControl = UniversalMeControlBase;
